'use client';

import Image from 'next/image';
import Link from 'next/link';
import Icon from '@/components/Icon';
import {
  ChangeEvent,
  ClipboardEvent,
  DragEvent,
  FormEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import type {
  DemoAdminEventType,
  IDemoAdminAttachment,
  IDemoAdminArtifact,
  IDemoAdminConfirm,
  IDemoAdminForm,
  IDemoAdminSession,
  IDemoAdminShowcase,
} from '@/interfaces/demoAdmin';
import {
  confirmDemoAdminChange,
  getDemoAdminShowcase,
  restartDemoAdminSession,
  respondToDemoAdminForm,
  startDemoAdminSession,
  streamDemoAdminMessage,
} from '@/lib/demoAdminApi';
import { buildDemoAdminShowcaseScenarios } from '@/lib/demoAdminShowcase';
import { renderChatMarkdownBlocks } from '@/lib/chatMarkdown';
import { trackInteraction } from '@/lib/analytics';
import type { THeroIcon } from '@/types';

interface AIOverviewSnippetProps {
  language: 'en' | 'zh';
}

type Scenario = 'business' | 'table' | 'staff' | 'try';

interface ChatMessage {
  id: number;
  role: 'assistant' | 'user';
  text: string;
  artifacts?: IDemoAdminArtifact[];
  suggestions?: string[];
  attachments?: DemoComposerAttachment[];
  confirm?: IDemoAdminConfirm & { decision?: boolean };
  form?: {
    requestId: string;
    value: IDemoAdminForm;
    answers?: Record<string, unknown>;
  };
}

interface DemoComposerAttachment {
  id: string;
  name: string;
  mediaType: string;
  data: string;
  previewUrl?: string;
}

interface DemoToolStep {
  tool: string;
  label: string;
  state: 'running' | 'error';
}

interface DemoPopularAction {
  icon: THeroIcon;
  label: string;
  prompt: string;
}

interface DemoPopularActionGroup {
  heading: string;
  items: DemoPopularAction[];
}

const scenarioOrder: Scenario[] = ['business', 'table', 'staff', 'try'];
const obieAnimatedAvatar = '/assets/images/ai/obie-warm.gif';
const xlsxMediaType =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const maxAttachments = 4;
const maxImageDimension = 1200;
const guidedFollowupMarker = '\n\n[Actual user follow-up]\n';
const demoQuickReplyClassName =
  'rounded-full border border-indigo-200 bg-white px-3 py-2 text-left text-[11px] font-semibold leading-4 text-indigo-700 transition hover:border-indigo-400 hover:bg-indigo-50 disabled:cursor-wait disabled:opacity-50 dark:border-indigo-800 dark:bg-gray-900 dark:text-indigo-200 dark:hover:bg-indigo-950';
const workingVerbs = {
  en: [
    'Thinking',
    'Pondering',
    'Working on it',
    'Digging in',
    'Crunching',
    'Connecting the dots',
    'Brewing',
    'Almost there',
  ],
  zh: [
    '思考中',
    '仔細思考中',
    '處理中',
    '深入查看中',
    '分析中',
    '整理資訊中',
    '準備回覆中',
    '就快完成了',
  ],
};

function visibleDemoUserMessage(message: string): string {
  const markerIndex = message.indexOf(guidedFollowupMarker);
  return markerIndex >= 0
    ? message.slice(markerIndex + guidedFollowupMarker.length)
    : message;
}

function DemoQuickReply({
  label,
  disabled = false,
  onSelect,
}: {
  label: string;
  disabled?: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      className={demoQuickReplyClassName}
    >
      {label}
    </button>
  );
}

const demoPopularActionGroups: Record<
  AIOverviewSnippetProps['language'],
  DemoPopularActionGroup[]
> = {
  en: [
    {
      heading: 'Bookings',
      items: [
        {
          icon: 'calendar',
          label: "What's booked tomorrow?",
          prompt: "What's booked tomorrow?",
        },
        {
          icon: 'user-group',
          label: "Who's attending today?",
          prompt: "Who's attending today?",
        },
        {
          icon: 'magnifying-glass',
          label: 'Which upcoming classes have space?',
          prompt: 'Which upcoming classes have space?',
        },
      ],
    },
    {
      heading: 'Analyse',
      items: [
        {
          icon: 'arrow-trending-down',
          label: 'Customers going quiet',
          prompt: 'Which regular customers are at risk of stopping?',
        },
        {
          icon: 'credit-card',
          label: 'No active passes',
          prompt: 'Which customers have no active passes?',
        },
        {
          icon: 'clock',
          label: 'Recently expired passes',
          prompt: 'Whose pass expired in the last 2 weeks?',
        },
      ],
    },
    {
      heading: 'Reports',
      items: [
        {
          icon: 'banknotes',
          label: 'Revenue breakdown',
          prompt: 'Show me our revenue over the last 3 months',
        },
        {
          icon: 'arrow-path',
          label: 'Member retention',
          prompt: "What's our member retention?",
        },
        {
          icon: 'chart-bar',
          label: 'Most popular classes',
          prompt: 'Which classes are most popular this quarter?',
        },
      ],
    },
    {
      heading: 'Take action',
      items: [
        {
          icon: 'user-plus',
          label: 'Book a customer into a class',
          prompt: 'Help me book a customer into a class',
        },
        {
          icon: 'gift',
          label: 'Give a customer a pass',
          prompt: 'Help me give a customer a pass',
        },
        {
          icon: 'check-circle',
          label: "Mark today's attendance",
          prompt: "Help me mark attendance for today's classes",
        },
      ],
    },
    {
      heading: 'Custom features',
      items: [
        {
          icon: 'squares-2x2',
          label: 'Explore custom features',
          prompt: 'Show me which custom features are available for my business',
        },
        {
          icon: 'pencil-square',
          label: 'Find out about intake forms',
          prompt: 'Tell me how intake forms could help my business',
        },
        {
          icon: 'credit-card',
          label: 'Find out about online payments',
          prompt:
            'Tell me how online payments work and how they could help my business',
        },
      ],
    },
    {
      heading: 'Learn',
      items: [
        {
          icon: 'ticket',
          label: 'How do passes work?',
          prompt: 'How do passes work?',
        },
        {
          icon: 'sparkles',
          label: 'What can my AI assistant do?',
          prompt: 'What can my AI assistant do?',
        },
        {
          icon: 'x-circle',
          label: 'How do cancellations work?',
          prompt: 'How do cancellations work?',
        },
      ],
    },
  ],
  zh: [
    {
      heading: '預約查詢',
      items: [
        {
          icon: 'calendar',
          label: '明天有哪些預約？',
          prompt: '明天有哪些預約？',
        },
        {
          icon: 'user-group',
          label: '今天有哪些人會出席？',
          prompt: '今天有哪些人會出席？',
        },
        {
          icon: 'magnifying-glass',
          label: '接下來哪些課程還有空位？',
          prompt: '接下來哪些課程還有空位？',
        },
      ],
    },
    {
      heading: '分析',
      items: [
        {
          icon: 'arrow-trending-down',
          label: '逐漸不再出席的顧客',
          prompt: '哪些常客最近可能不再出席？',
        },
        {
          icon: 'credit-card',
          label: '沒有有效票券的顧客',
          prompt: '哪些顧客目前沒有有效票券？',
        },
        {
          icon: 'clock',
          label: '最近到期的票券',
          prompt: '過去兩週有哪些顧客的票券到期？',
        },
      ],
    },
    {
      heading: '報表',
      items: [
        {
          icon: 'banknotes',
          label: '營收分析',
          prompt: '顯示過去 3 個月的營收分析',
        },
        {
          icon: 'arrow-path',
          label: '會員留存率',
          prompt: '我們的會員留存率是多少？',
        },
        {
          icon: 'chart-bar',
          label: '最熱門課程',
          prompt: '本季最熱門的是哪些課程？',
        },
      ],
    },
    {
      heading: '執行操作',
      items: [
        {
          icon: 'user-plus',
          label: '幫顧客預約課程',
          prompt: '幫我替一位顧客預約課程',
        },
        {
          icon: 'gift',
          label: '發送票券給顧客',
          prompt: '幫我發送一張票券給顧客',
        },
        {
          icon: 'check-circle',
          label: '記錄今天的出席狀況',
          prompt: '幫我記錄今天課程的出席狀況',
        },
      ],
    },
    {
      heading: '自訂功能',
      items: [
        {
          icon: 'squares-2x2',
          label: '探索自訂功能',
          prompt: '顯示我的商家可以使用哪些自訂功能',
        },
        {
          icon: 'pencil-square',
          label: '了解資料填寫表單',
          prompt: '告訴我資料填寫表單可以如何幫助我的商家',
        },
        {
          icon: 'credit-card',
          label: '了解線上付款',
          prompt: '告訴我線上付款如何運作，以及可以如何幫助我的商家',
        },
      ],
    },
    {
      heading: '了解',
      items: [
        {
          icon: 'ticket',
          label: '票券如何運作？',
          prompt: '票券如何運作？',
        },
        {
          icon: 'sparkles',
          label: 'AI 助理可以幫我做什麼？',
          prompt: 'AI 助理可以幫我做什麼？',
        },
        {
          icon: 'x-circle',
          label: '取消預約如何運作？',
          prompt: '取消預約如何運作？',
        },
      ],
    },
  ],
};

function attachmentMediaType(file: File): string | null {
  if (
    ['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(file.type)
  ) {
    return file.type;
  }
  if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
    return 'text/csv';
  }
  if (
    file.type === xlsxMediaType ||
    file.name.toLowerCase().endsWith('.xlsx')
  ) {
    return xlsxMediaType;
  }
  return null;
}

function readFileDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () =>
      reject(reader.error ?? new Error('File read failed.'));
    reader.readAsDataURL(file);
  });
}

async function prepareDemoAttachment(
  file: File
): Promise<DemoComposerAttachment | null> {
  const mediaType = attachmentMediaType(file);
  if (!mediaType) return null;
  if (mediaType === 'text/csv' && file.size > 1_400_000) return null;
  if (mediaType === xlsxMediaType && file.size > 5_000_000) return null;

  let dataUrl = await readFileDataUrl(file);
  if (
    mediaType.startsWith('image/') &&
    mediaType !== 'image/gif' &&
    file.size > 150_000
  ) {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new window.Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('Image decode failed.'));
      element.src = dataUrl;
    });
    const scale = Math.min(
      1,
      maxImageDimension / Math.max(image.width, image.height)
    );
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));
    canvas
      .getContext('2d')
      ?.drawImage(image, 0, 0, canvas.width, canvas.height);
    dataUrl = canvas.toDataURL('image/jpeg', 0.65);
  }

  const normalizedMediaType = dataUrl.startsWith('data:image/jpeg')
    ? 'image/jpeg'
    : mediaType;
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    name: file.name.slice(0, 200),
    mediaType: normalizedMediaType,
    data: dataUrl.slice(dataUrl.indexOf(',') + 1),
    ...(normalizedMediaType.startsWith('image/')
      ? { previewUrl: dataUrl }
      : {}),
  };
}

const copy = {
  en: {
    eyebrow: 'AI, built into the work',
    title: 'Ask the business. Get an answer you can act on.',
    description:
      'Move from a question to the right records, a clear explanation, and the next action—all without leaving Omcean Booking.',
    explore: 'Explore AI in Omcean Booking',
    assistant: 'Obie',
    assistantRole: 'Admin AI assistant',
    online: 'Ready to help',
    guidedExample: 'Guided example',
    ask: 'Ask Obie about your business…',
    openInteractive: 'Open the interactive demo',
    askNext: 'Ask next',
    scenarios: {
      business: {
        label: 'Business insights',
        caption: 'Understand performance across your business',
      },
      table: {
        label: 'Table assistance',
        caption: 'Work with the records already on screen',
      },
      staff: {
        label: 'Staff & services',
        caption: 'Connect real staff to services and availability',
      },
      try: {
        label: 'Try it now!',
        caption: 'Ask Obie a question in this interactive demo',
      },
    },
    interactive: {
      badge: 'Interactive demo',
      welcome:
        "Hi, I'm Obie. You're signed in with a demo Admin account for the Omcean Booking demo business. I can query its live bookings, customers, schedules, and performance, or make changes when you approve them. Anything you change affects demo data only and is reset nightly.",
      quickReplies: [
        {
          label: 'Review business health',
          prompt:
            'Give me a concise health check for the Demo business. Review recent no-shows, cancellations, full classes, and waiting lists, support the main finding with current figures, and recommend the single best next action.',
        },
        {
          label: 'Draft a customer check-in',
          prompt:
            'Use recent no-show and cancellation activity to identify the single customer who would benefit most from a personal follow-up. Explain why, then draft a short friendly message. Do not send it.',
        },
        {
          label: 'Find an appointment time',
          prompt:
            'Choose an appointment service offered by an active staff member and show me their next five available appointment times. Do not book anything.',
        },
      ],
      tryAsking: 'Try asking',
      popular: 'Popular actions',
      searchActions: 'Search actions…',
      noMatchingActions: 'No matching actions',
      attach: 'Attach images or spreadsheets',
      restart: 'Restart conversation',
      attachmentError:
        'Use up to four PNG, JPEG, WebP, GIF, CSV, or XLSX files within the size limits.',
      typing: 'Obie is checking your business…',
      connecting: 'Connecting to the demo Admin account…',
      unavailable:
        'The live demo is temporarily unavailable. Please try again in a moment.',
      send: 'Send message',
      approve: 'Approve change',
      reject: 'Keep as is',
      approved: 'Approved',
      rejected: 'Not applied',
      submit: 'Continue',
    },
  },
  zh: {
    eyebrow: 'AI 直接融入工作',
    title: '向商家提問，立即取得可執行的答案',
    description:
      '從問題、正確紀錄、清楚說明到下一步操作，全都不必離開 Omcean Booking。',
    explore: '探索 Omcean Booking AI',
    assistant: 'Obie',
    assistantRole: '管理端 AI 智能助理',
    online: '隨時為您服務',
    guidedExample: '功能示範',
    ask: '向 Obie 詢問營運問題⋯',
    openInteractive: '開啟互動示範',
    askNext: '接著詢問',
    scenarios: {
      business: {
        label: '營運洞察',
        caption: '掌握整體商家的營運表現',
      },
      table: {
        label: '表格助理',
        caption: '直接分析畫面上的紀錄',
      },
      staff: {
        label: '員工與服務',
        caption: '查看實際員工、服務與可預約時段',
      },
      try: {
        label: '立即試用！',
        caption: '在互動示範中直接向 Obie 提問',
      },
    },
    interactive: {
      badge: '互動示範',
      welcome:
        '您好，我是 Obie。您目前使用 Omcean Booking 示範商家的「Admin」管理員帳戶。我可以查詢實際的預約、顧客、排程與營運表現，也能在您核准後修改示範商家的資料。所有變更只會影響示範資料，並會於每晚重設。',
      quickReplies: [
        {
          label: '檢視商家營運狀況',
          prompt:
            '請簡潔檢視示範商家的營運狀況，查看近期未出席、取消預約、額滿課程與候補名單，以目前數據說明最重要的發現，並建議一項最值得優先採取的行動。',
        },
        {
          label: '草擬顧客關懷訊息',
          prompt:
            '根據近期未出席與取消預約紀錄，找出最需要個人關懷的一位顧客，說明原因，並草擬一則簡短友善的訊息。請勿傳送。',
        },
        {
          label: '尋找可預約時段',
          prompt:
            '選擇一項由現職員工提供的預約服務，列出該員工接下來五個可預約時段。請勿進行預約。',
        },
      ],
      tryAsking: '試著詢問',
      popular: '常用操作',
      searchActions: '搜尋操作⋯',
      noMatchingActions: '找不到符合的操作',
      attach: '附加圖片或試算表',
      restart: '重新開始對話',
      attachmentError:
        '最多可附加四個符合大小限制的 PNG、JPEG、WebP、GIF、CSV 或 XLSX 檔案。',
      typing: 'Obie 正在查看商家資料⋯',
      connecting: '正在連線至示範 Admin 帳戶⋯',
      unavailable: '互動示範目前暫時無法使用，請稍後再試。',
      send: '傳送訊息',
      approve: '核准變更',
      reject: '維持原狀',
      approved: '已核准',
      rejected: '未套用',
      submit: '繼續',
    },
  },
};

function DemoArtifactTable({ artifact }: { artifact: IDemoAdminArtifact }) {
  return (
    <div className="mt-3 max-w-full overflow-hidden rounded-xl border border-indigo-100 bg-white dark:border-indigo-900 dark:bg-gray-900">
      <div className="border-b border-gray-100 px-3 py-2 text-xs font-bold text-gray-800 dark:border-gray-800 dark:text-gray-100">
        {artifact.title}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[440px] border-collapse text-left text-[11px]">
          <thead className="bg-gray-50 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
            <tr>
              {artifact.columns.map(column => (
                <th key={column} className="px-3 py-2 font-semibold">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {artifact.rows.slice(0, 8).map((row, rowIndex) => (
              <tr
                key={`${artifact.source}-${rowIndex}`}
                className="border-t border-gray-100 dark:border-gray-800"
              >
                {row.map((cell, columnIndex) => (
                  <td
                    key={columnIndex}
                    className="whitespace-nowrap px-3 py-2 text-gray-700 dark:text-gray-300"
                  >
                    {cell ?? '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DemoConfirmCard({
  confirmation,
  labels,
  disabled,
  onDecision,
}: {
  confirmation: IDemoAdminConfirm & { decision?: boolean };
  labels: {
    approve: string;
    reject: string;
    approved: string;
    rejected: string;
  };
  disabled: boolean;
  onDecision: (approved: boolean) => void;
}) {
  const toolLabel = confirmation.change.tool.replaceAll('_', ' ');
  const fields = Object.entries(confirmation.change.args).slice(0, 5);

  return (
    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/30">
      <p className="escape !text-[11px] !leading-4 font-bold capitalize text-amber-950 dark:text-amber-100">
        {toolLabel}
      </p>
      {fields.length > 0 && (
        <dl className="mt-2 space-y-1">
          {fields.map(([key, value]) => (
            <div
              key={key}
              className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-2 text-[10px] leading-4"
            >
              <dt className="truncate text-amber-700 dark:text-amber-300">
                {key.replaceAll('_', ' ')}
              </dt>
              <dd className="break-words font-medium text-amber-950 dark:text-amber-100">
                {typeof value === 'object'
                  ? JSON.stringify(value)
                  : String(value)}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {confirmation.decision === undefined ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={disabled}
            onClick={() => onDecision(true)}
            className="rounded-lg bg-indigo-600 px-3 py-2 text-[11px] font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {labels.approve}
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onDecision(false)}
            className="rounded-lg border border-amber-300 bg-white px-3 py-2 text-[11px] font-bold text-amber-900 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-800 dark:bg-gray-900 dark:text-amber-100"
          >
            {labels.reject}
          </button>
        </div>
      ) : (
        <p className="escape mt-2 !text-[11px] !leading-4 font-bold text-amber-900 dark:text-amber-100">
          {confirmation.decision ? labels.approved : labels.rejected}
        </p>
      )}
    </div>
  );
}

function DemoFormCard({
  form,
  submitLabel,
  onSubmit,
}: {
  form: ChatMessage['form'];
  submitLabel: string;
  onSubmit: (answers: Record<string, unknown>) => Promise<void>;
}) {
  const questions = form?.value.questions ?? [];
  const [answers, setAnswers] = useState<Record<string, unknown>>(() =>
    Object.fromEntries(
      questions
        .filter(question => question.id && question.recommended !== undefined)
        .map(question => [question.id as string, question.recommended])
    )
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!form) return null;

  const setAnswer = (id: string, value: unknown) => {
    setAnswers(current => ({ ...current, [id]: value }));
  };
  const submittedAnswers = form.answers;

  return (
    <form
      className="mt-3 rounded-xl border border-indigo-200 bg-white p-3 dark:border-indigo-800 dark:bg-gray-900"
      onSubmit={event => {
        event.preventDefault();
        if (submittedAnswers || !form.requestId) return;
        setSubmitting(true);
        setError(null);
        void onSubmit(answers)
          .catch(reason =>
            setError(
              reason instanceof Error ? reason.message : 'Unable to continue.'
            )
          )
          .finally(() => setSubmitting(false));
      }}
    >
      {form.value.title && (
        <p className="escape !text-xs !leading-4 font-bold text-gray-900 dark:text-white">
          {form.value.title}
        </p>
      )}
      {form.value.description && (
        <p className="escape mt-1 !text-[11px] !leading-4 text-gray-500 dark:text-gray-400">
          {form.value.description}
        </p>
      )}
      <div className="mt-3 space-y-3">
        {questions.map((question, index) => {
          const id = question.id || `question_${index}`;
          const label = question.question || question.label || id;
          const value = submittedAnswers?.[id] ?? answers[id] ?? '';
          const options = (question.options ?? []).map(option =>
            typeof option === 'object'
              ? {
                  label: option.label ?? String(option.value ?? ''),
                  value: option.value ?? option.label ?? '',
                }
              : { label: String(option), value: option }
          );
          if (question.type === 'confirm' || question.type === 'boolean') {
            options.splice(
              0,
              options.length,
              { label: 'Yes', value: true },
              { label: 'No', value: false }
            );
          }
          return (
            <label key={id} className="block">
              <span className="mb-1 block text-[11px] font-semibold text-gray-700 dark:text-gray-200">
                {label}
              </span>
              {options.length > 0 ? (
                <select
                  value={String(value)}
                  required={question.required}
                  disabled={Boolean(submittedAnswers)}
                  onChange={event => {
                    const selected = options.find(
                      option => String(option.value) === event.target.value
                    );
                    setAnswer(id, selected?.value ?? event.target.value);
                  }}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-900 outline-none focus:border-indigo-400 dark:border-gray-700 dark:bg-gray-950 dark:text-white"
                >
                  <option value="">—</option>
                  {options.map(option => (
                    <option
                      key={`${id}-${String(option.value)}`}
                      value={String(option.value)}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={question.type === 'number' ? 'number' : 'text'}
                  value={String(value)}
                  required={question.required}
                  disabled={Boolean(submittedAnswers)}
                  onChange={event =>
                    setAnswer(
                      id,
                      question.type === 'number'
                        ? Number(event.target.value)
                        : event.target.value
                    )
                  }
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-900 outline-none focus:border-indigo-400 dark:border-gray-700 dark:bg-gray-950 dark:text-white"
                />
              )}
            </label>
          );
        })}
      </div>
      {!submittedAnswers && form.requestId && (
        <button
          type="submit"
          disabled={submitting}
          className="mt-3 rounded-lg bg-indigo-600 px-3 py-2 text-[11px] font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {submitLabel}
        </button>
      )}
      {error && (
        <p className="escape mt-2 !text-[11px] !leading-4 text-rose-600 dark:text-rose-300">
          {error}
        </p>
      )}
    </form>
  );
}

type AdminDemoSendSource =
  | 'typed'
  | 'guided_followup'
  | 'suggestion'
  | 'quick_reply'
  | 'popular_action';

export default function AIOverviewSnippet({
  language,
}: AIOverviewSnippetProps) {
  const text = copy[language];
  const [activeScenario, setActiveScenario] = useState<Scenario>('business');
  const [chatMessagesByScenario, setChatMessagesByScenario] = useState<
    Record<Scenario, ChatMessage[]>
  >({
    business: [],
    table: [],
    staff: [],
    try: [{ id: 1, role: 'assistant', text: text.interactive.welcome }],
  });
  const [input, setInput] = useState('');
  const [draftAttachments, setDraftAttachments] = useState<
    DemoComposerAttachment[]
  >([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [draggingAttachment, setDraggingAttachment] = useState(false);
  const [popularActionsOpen, setPopularActionsOpen] = useState(false);
  const [popularActionQuery, setPopularActionQuery] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const [activity, setActivity] = useState<string | null>(null);
  const [toolSteps, setToolSteps] = useState<DemoToolStep[]>([]);
  const [workingVerbIndex, setWorkingVerbIndex] = useState(0);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [showcase, setShowcase] = useState<IDemoAdminShowcase | null>(null);
  const [showcaseLoadFailed, setShowcaseLoadFailed] = useState(false);
  const nextMessageId = useRef(2);
  const adminSessionRefs = useRef<Partial<Record<Scenario, IDemoAdminSession>>>(
    {}
  );
  const sessionPromiseRefs = useRef<
    Partial<Record<Scenario, Promise<IDemoAdminSession>>>
  >({});
  const scenarioHasUserTurnRefs = useRef<Partial<Record<Scenario, boolean>>>(
    {}
  );
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);

  /** Website telemetry for the Admin AI demo (never message text). */
  const trackDemo = (
    type: Parameters<typeof trackInteraction>[0],
    value: string | null = null,
    data: Record<string, unknown> = {}
  ) => trackInteraction(type, value, { language, ...data });

  useEffect(() => {
    const section = sectionRef.current;
    if (!section || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        observer.disconnect();
        trackInteraction('admin_demo_viewed', null, { language });
      },
      { threshold: 0.3 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, [language]);
  const guidedScenarios = buildDemoAdminShowcaseScenarios(language, showcase);
  const interactiveQuickReplies = showcase
    ? language === 'zh'
      ? [
          {
            label: '檢視商家營運狀況',
            prompt: `請檢視示範商家的營運狀況：分析 ${showcase.no_show_period.date_from} 至 ${showcase.no_show_period.date_to} 的未出席、${showcase.cancellation_period.date_from} 至 ${showcase.cancellation_period.date_to} 的取消預約，以及 ${showcase.upcoming_period.date_from} 至 ${showcase.upcoming_period.date_to} 的額滿課程與候補名單。請用目前數據說明最重要的發現，並建議一項最值得優先採取的行動。`,
          },
          {
            label: '草擬顧客關懷訊息',
            prompt: guidedScenarios.table.followups[2].livePrompt,
          },
          {
            label: '尋找可預約時段',
            prompt: guidedScenarios.staff.followups[1].livePrompt,
          },
        ]
      : [
          {
            label: 'Review business health',
            prompt: `Give me a concise health check for the Demo business. Review no-shows from ${showcase.no_show_period.date_from} through ${showcase.no_show_period.date_to}, cancellations from ${showcase.cancellation_period.date_from} through ${showcase.cancellation_period.date_to}, and full classes and waiting lists from ${showcase.upcoming_period.date_from} through ${showcase.upcoming_period.date_to}. Support the main finding with current figures and recommend the single best next action.`,
          },
          {
            label: 'Draft a customer check-in',
            prompt: guidedScenarios.table.followups[2].livePrompt,
          },
          {
            label: 'Find an appointment time',
            prompt: guidedScenarios.staff.followups[1].livePrompt,
          },
        ]
    : text.interactive.quickReplies;
  const guidedScenario =
    activeScenario === 'try' ? null : guidedScenarios[activeScenario];
  const businessChartMax = Math.max(
    1,
    ...guidedScenarios.business.chart.map(item => item.value)
  );
  const staffInitials = guidedScenarios.staff.instructorName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('');
  const appointmentFlow =
    language === 'zh'
      ? ['顧客選擇服務', '配對服務人員', '顯示即時時段']
      : [
          'Customer picks a service',
          'Staff member is matched',
          'Live times are offered',
        ];
  const chatMessages = chatMessagesByScenario[activeScenario];
  const guidedAgentMessage = (
    scenario: Exclude<Scenario, 'try'>,
    message: string
  ) => {
    const example = guidedScenarios[scenario];
    let supportingContext = '';
    if (scenario === 'table') {
      supportingContext = `\nThe table shown in the example was:\n${guidedScenarios.table.tableRows
        .map(row => row.join(' | '))
        .join('\n')}`;
    } else if (scenario === 'staff') {
      supportingContext = `\nThe service cards shown were: ${guidedScenarios.staff.steps
        .map(step => `${step.title}: ${step.detail}`)
        .join('; ')}.`;
    }
    return `${
      language === 'zh'
        ? '這是網站管理端 AI 示範中既有對話的後續提問。'
        : 'This is a follow-up to an existing guided Admin AI example on the website.'
    }\nThe record names and IDs below are exact current Demoapt database values, not placeholders. Preserve them exactly; do not anonymize or translate them. Use fresh business-tool reads when the follow-up asks for data beyond the values shown below.\n${language === 'zh' ? '原始問題' : 'Original question'}: ${
      example.prompt
    }\n${language === 'zh' ? 'Obie 先前的回答' : "Obie's previous answer"}: ${
      example.responseTitle
    }. ${example.response}${supportingContext}${guidedFollowupMarker}${
      message ||
      (language === 'zh'
        ? '請查看我附加的檔案。'
        : 'Please review the attached files.')
    }`;
  };

  useEffect(() => {
    nextMessageId.current = 2;
    adminSessionRefs.current = {};
    sessionPromiseRefs.current = {};
    scenarioHasUserTurnRefs.current = {};
    setChatMessagesByScenario({
      business: [],
      table: [],
      staff: [],
      try: [{ id: 1, role: 'assistant', text: text.interactive.welcome }],
    });
    setInput('');
    setDraftAttachments([]);
    setAttachmentError(null);
    setDraggingAttachment(false);
    setPopularActionsOpen(false);
    setPopularActionQuery('');
    setIsReplying(false);
    setActivity(null);
    setToolSteps([]);
    setSessionError(null);
  }, [language, text.interactive.welcome]);

  useEffect(() => {
    let active = true;
    getDemoAdminShowcase()
      .then(data => {
        if (!active) return;
        setShowcase(data);
        setShowcaseLoadFailed(false);
      })
      .catch(() => {
        if (!active) return;
        setShowcaseLoadFailed(true);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    threadRef.current?.scrollTo({
      top: threadRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [activeScenario, chatMessages, isReplying]);

  useEffect(() => {
    if (!isReplying) return;
    const verbs = workingVerbs[language];
    setWorkingVerbIndex(Math.floor(Math.random() * verbs.length));
    const timer = window.setInterval(() => {
      setWorkingVerbIndex(current => {
        const offset = 1 + Math.floor(Math.random() * (verbs.length - 1));
        return (current + offset) % verbs.length;
      });
    }, 2600);
    return () => window.clearInterval(timer);
  }, [isReplying, language]);

  const updateScenarioMessages = (
    scenario: Scenario,
    update: (messages: ChatMessage[]) => ChatMessage[]
  ) => {
    setChatMessagesByScenario(current => ({
      ...current,
      [scenario]: update(current[scenario]),
    }));
  };

  const hydrateSession = (scenario: Scenario, session: IDemoAdminSession) => {
    scenarioHasUserTurnRefs.current[scenario] = session.history.some(
      message => message.agent_message_role === 'user'
    );
    const history = session.history
      .filter(message =>
        ['user', 'assistant', 'human_agent'].includes(
          message.agent_message_role
        )
      )
      .map<ChatMessage>(message => ({
        id: nextMessageId.current++,
        role: message.agent_message_role === 'user' ? 'user' : 'assistant',
        text:
          message.agent_message_role === 'user'
            ? visibleDemoUserMessage(message.content)
            : message.content,
        ...(message.artifacts?.length ? { artifacts: message.artifacts } : {}),
        ...(message.suggestions?.length
          ? { suggestions: message.suggestions }
          : {}),
        ...(message.images?.length || message.files?.length
          ? {
              attachments: [
                ...(message.images ?? []).map((url, index) => ({
                  id: `history-image-${index}-${url}`,
                  name: `Image ${index + 1}`,
                  mediaType: 'image/jpeg',
                  data: '',
                  previewUrl: url,
                })),
                ...(message.files ?? []).map((file, index) => ({
                  id: `history-file-${index}-${file.name}`,
                  name: file.name,
                  mediaType: 'text/csv',
                  data: '',
                })),
              ],
            }
          : {}),
        ...(message.form
          ? {
              form: {
                requestId: '',
                value: message.form,
                answers: message.form_answers,
              },
            }
          : {}),
        ...(message.confirm
          ? {
              confirm: {
                requestId: '',
                change: message.confirm,
                decision: message.confirm_rejected ? false : undefined,
              },
            }
          : {}),
      }));
    const latestSuggestionsIndex = history
      .map(message => Boolean(message.suggestions?.length))
      .lastIndexOf(true);
    history.forEach((message, index) => {
      if (index !== latestSuggestionsIndex) delete message.suggestions;
    });
    const pending = session.pending_confirms.map<ChatMessage>(item => ({
      id: nextMessageId.current++,
      role: 'assistant',
      text: '',
      confirm: {
        requestId: item.requestId,
        change: { tool: item.tool, args: item.args },
      },
    }));
    const pendingForm: ChatMessage[] = session.pending_form
      ? [
          {
            id: nextMessageId.current++,
            role: 'assistant',
            text: '',
            form: {
              requestId: session.pending_form.request_id,
              value: session.pending_form.form,
            },
          },
        ]
      : [];
    const openingMessages: ChatMessage[] =
      scenario === 'try'
        ? [{ id: 1, role: 'assistant', text: text.interactive.welcome }]
        : [];
    updateScenarioMessages(scenario, () => [
      ...openingMessages,
      ...history,
      ...pending,
      ...pendingForm,
    ]);
  };

  const ensureAdminSession = async (
    scenario: Scenario
  ): Promise<IDemoAdminSession> => {
    if (adminSessionRefs.current[scenario])
      return adminSessionRefs.current[scenario];
    if (sessionPromiseRefs.current[scenario])
      return sessionPromiseRefs.current[scenario];

    setActivity(text.interactive.connecting);
    setSessionError(null);
    const promise = startDemoAdminSession(language, scenario);
    sessionPromiseRefs.current[scenario] = promise;
    try {
      const session = await promise;
      adminSessionRefs.current[scenario] = session;
      hydrateSession(scenario, session);
      return session;
    } catch (error) {
      setSessionError(
        error instanceof Error ? error.message : text.interactive.unavailable
      );
      throw error;
    } finally {
      delete sessionPromiseRefs.current[scenario];
      setActivity(null);
    }
  };

  const updateAssistantMessage = (
    scenario: Scenario,
    id: number,
    update: (message: ChatMessage) => ChatMessage
  ) => {
    updateScenarioMessages(scenario, current =>
      current.map(message => (message.id === id ? update(message) : message))
    );
  };

  const handleStreamEvent = (
    scenario: Scenario,
    id: number,
    event: DemoAdminEventType
  ) => {
    if (event.type === 'status') {
      if (event.status === 'tool' && event.tool) {
        setActivity(null);
        setToolSteps(current => [
          ...current,
          {
            tool: event.tool as string,
            label:
              typeof event.message === 'string'
                ? event.message
                : text.interactive.typing,
            state: 'running',
          },
        ]);
      } else {
        setActivity(typeof event.message === 'string' ? event.message : null);
      }
      return;
    }
    if (event.type === 'tool_result') {
      setActivity(null);
      setToolSteps(current => {
        const reverseIndex = [...current]
          .reverse()
          .findIndex(
            step => step.tool === event.tool && step.state === 'running'
          );
        const index = reverseIndex < 0 ? -1 : current.length - reverseIndex - 1;
        if (index < 0) return current;
        if (event.ok)
          return current.filter((_, itemIndex) => itemIndex !== index);
        return current.map((step, itemIndex) =>
          itemIndex === index ? { ...step, state: 'error' } : step
        );
      });
      return;
    }
    if (event.type === 'delta') {
      setActivity(null);
      updateAssistantMessage(scenario, id, message => ({
        ...message,
        text: `${message.text}${event.text}`,
      }));
      return;
    }
    if (event.type === 'artifact') {
      updateAssistantMessage(scenario, id, message => ({
        ...message,
        artifacts: [...(message.artifacts ?? []), event.artifact],
      }));
      return;
    }
    if (event.type === 'suggestions') {
      updateAssistantMessage(scenario, id, message => ({
        ...message,
        suggestions: event.suggestions,
      }));
      return;
    }
    if (event.type === 'confirm') {
      setActivity(null);
      updateAssistantMessage(scenario, id, message => ({
        ...message,
        confirm: { requestId: event.requestId, change: event.change },
      }));
      return;
    }
    if (event.type === 'form') {
      setActivity(null);
      updateAssistantMessage(scenario, id, message => ({
        ...message,
        form: { requestId: event.requestId, value: event.form },
      }));
      return;
    }
    if (event.type === 'done') {
      setActivity(null);
      setToolSteps(current => current.filter(step => step.state === 'error'));
      if (event.text) {
        updateAssistantMessage(scenario, id, message => ({
          ...message,
          text: event.text,
        }));
      }
      return;
    }
    if (event.type === 'error') {
      setActivity(null);
      setToolSteps(current =>
        current.map(step =>
          step.state === 'running' ? { ...step, state: 'error' } : step
        )
      );
      updateAssistantMessage(scenario, id, message => ({
        ...message,
        text: event.message || text.interactive.unavailable,
      }));
    }
  };

  const runAssistantStream = async (
    scenario: Scenario,
    operation: (
      session: IDemoAdminSession,
      onEvent: (event: DemoAdminEventType) => void
    ) => Promise<void>
  ) => {
    const assistantId = nextMessageId.current++;
    updateScenarioMessages(scenario, current => [
      ...current,
      { id: assistantId, role: 'assistant', text: '' },
    ]);
    setIsReplying(true);
    setActivity(null);
    setToolSteps([]);
    setSessionError(null);
    const startedAt = Date.now();
    let outcomeTracked = false;
    const trackOutcome = (failed: boolean, error?: string) => {
      if (outcomeTracked) return;
      outcomeTracked = true;
      trackDemo(
        failed ? 'admin_demo_reply_failed' : 'admin_demo_reply_received',
        scenario,
        {
          latency_ms: Date.now() - startedAt,
          ...(error ? { error: error.slice(0, 200) } : {}),
        }
      );
    };
    try {
      const session = await ensureAdminSession(scenario);
      await operation(session, event => {
        if (event.type === 'done') trackOutcome(false);
        if (event.type === 'error') trackOutcome(true, event.message);
        handleStreamEvent(scenario, assistantId, event);
      });
    } catch (error) {
      trackOutcome(
        true,
        error instanceof Error ? error.message : String(error)
      );
      updateAssistantMessage(scenario, assistantId, message => ({
        ...message,
        text:
          error instanceof Error ? error.message : text.interactive.unavailable,
      }));
    } finally {
      setIsReplying(false);
      setActivity(null);
      window.requestAnimationFrame(() =>
        inputRef.current?.focus({ preventScroll: true })
      );
    }
  };

  const selectScenario = (scenario: Scenario) => {
    if (scenario !== activeScenario) {
      trackDemo('admin_demo_scenario_selected', scenario);
    }
    setActiveScenario(scenario);
    setPopularActionsOpen(false);
    setPopularActionQuery('');
    void ensureAdminSession(scenario).catch(() => undefined);
  };

  const clearQuickReplies = (scenario: Scenario) => {
    updateScenarioMessages(scenario, current =>
      current.map(message =>
        message.suggestions ? { ...message, suggestions: undefined } : message
      )
    );
  };

  const addAttachments = async (files: File[]) => {
    setAttachmentError(null);
    const available = Math.max(0, maxAttachments - draftAttachments.length);
    if (available === 0) {
      setAttachmentError(text.interactive.attachmentError);
      return;
    }
    try {
      const prepared = (
        await Promise.all(files.slice(0, available).map(prepareDemoAttachment))
      ).filter((item): item is DemoComposerAttachment => Boolean(item));
      const totalSize = [...draftAttachments, ...prepared].reduce(
        (total, item) => total + item.data.length,
        0
      );
      if (
        files.length > available ||
        prepared.length !== Math.min(files.length, available) ||
        totalSize > 9_000_000
      ) {
        setAttachmentError(text.interactive.attachmentError);
      }
      if (totalSize <= 9_000_000) {
        setDraftAttachments(current => [...current, ...prepared]);
      }
    } catch {
      setAttachmentError(text.interactive.attachmentError);
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    void addAttachments(files);
  };

  const handleAttachmentDrop = (event: DragEvent<HTMLFormElement>) => {
    event.preventDefault();
    setDraggingAttachment(false);
    if (isReplying) return;
    void addAttachments(Array.from(event.dataTransfer.files ?? []));
  };

  const handleAttachmentPaste = (
    event: ClipboardEvent<HTMLTextAreaElement>
  ) => {
    const files = Array.from(event.clipboardData.items)
      .filter(item => item.kind === 'file')
      .map(item => item.getAsFile())
      .filter((file): file is File => Boolean(file));
    if (files.length === 0) return;
    event.preventDefault();
    void addAttachments(files);
  };

  const sendMessage = async (
    value: string,
    attachments: DemoComposerAttachment[] = [],
    scenario: Scenario = activeScenario,
    source: AdminDemoSendSource = 'typed'
  ) => {
    const message = value.trim();
    if ((!message && attachments.length === 0) || isReplying) return;
    trackDemo('admin_demo_message_sent', scenario, {
      source,
      attachments: attachments.length,
    });

    setIsReplying(true);
    try {
      await ensureAdminSession(scenario);
    } catch {
      setIsReplying(false);
      return;
    }
    const shouldSeedGuidedContext =
      scenario !== 'try' && !scenarioHasUserTurnRefs.current[scenario];
    const agentMessage = shouldSeedGuidedContext
      ? guidedAgentMessage(scenario, message)
      : message;
    scenarioHasUserTurnRefs.current[scenario] = true;
    clearQuickReplies(scenario);
    setPopularActionsOpen(false);
    updateScenarioMessages(scenario, current => [
      ...current,
      {
        id: nextMessageId.current++,
        role: 'user',
        text: message,
        ...(attachments.length ? { attachments } : {}),
      },
    ]);
    setInput('');
    setDraftAttachments([]);
    setAttachmentError(null);
    await runAssistantStream(scenario, (session, onEvent) =>
      streamDemoAdminMessage(
        session,
        language,
        agentMessage,
        attachments.map<IDemoAdminAttachment>(attachment => ({
          media_type: attachment.mediaType,
          data: attachment.data,
          name: attachment.name,
        })),
        onEvent
      )
    );
  };

  const sendInActiveConversation = (
    value: string,
    attachments: DemoComposerAttachment[] = [],
    source: AdminDemoSendSource = 'typed'
  ) => {
    setPopularActionsOpen(false);
    setPopularActionQuery('');
    void sendMessage(value, attachments, activeScenario, source);
  };

  const restartConversation = async () => {
    if (isReplying) return;
    trackDemo('admin_demo_restarted', activeScenario);
    setIsReplying(true);
    setPopularActionsOpen(false);
    setPopularActionQuery('');
    setActivity(text.interactive.connecting);
    setSessionError(null);
    setToolSteps([]);
    try {
      const scenario = activeScenario;
      const session = await restartDemoAdminSession(language, scenario);
      adminSessionRefs.current[scenario] = session;
      delete sessionPromiseRefs.current[scenario];
      hydrateSession(scenario, session);
      setInput('');
      setDraftAttachments([]);
      setAttachmentError(null);
    } catch (error) {
      setSessionError(
        error instanceof Error ? error.message : text.interactive.unavailable
      );
    } finally {
      setIsReplying(false);
      setActivity(null);
      window.requestAnimationFrame(() =>
        inputRef.current?.focus({ preventScroll: true })
      );
    }
  };

  const decideChange = async (
    messageId: number,
    confirmation: IDemoAdminConfirm,
    approved: boolean
  ) => {
    if (isReplying || !confirmation.requestId) return;
    const scenario = activeScenario;
    trackDemo('admin_demo_approval', approved ? 'approved' : 'declined', {
      scenario,
      tool: confirmation.change.tool,
    });
    updateAssistantMessage(scenario, messageId, message => ({
      ...message,
      confirm: { ...confirmation, decision: approved },
    }));
    await runAssistantStream(scenario, (session, onEvent) =>
      confirmDemoAdminChange(
        session,
        language,
        confirmation.requestId,
        approved,
        onEvent
      )
    );
  };

  const submitForm = async (
    messageId: number,
    requestId: string,
    answers: Record<string, unknown>
  ) => {
    if (!requestId) return;
    const scenario = activeScenario;
    trackDemo('admin_demo_form_submitted', scenario, {
      answers: Object.keys(answers).length,
    });
    const session = await ensureAdminSession(scenario);
    const result = await respondToDemoAdminForm(
      session,
      language,
      requestId,
      answers
    );
    updateAssistantMessage(scenario, messageId, message => ({
      ...message,
      form: message.form ? { ...message.form, answers } : undefined,
    }));
    if (result.snapshot_cleared) {
      const form = chatMessages.find(message => message.id === messageId)?.form;
      const answerText = Object.entries(answers)
        .map(([id, value]) => {
          const question = form?.value.questions?.find(item => item.id === id);
          return `${question?.question || question?.label || id}: ${
            Array.isArray(value) ? value.join(', ') : String(value)
          }`;
        })
        .join('; ');
      await sendMessage(
        `${language === 'zh' ? '表單已提交' : 'Form submitted'} — ${answerText}`,
        [],
        scenario
      );
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    sendInActiveConversation(input, draftAttachments);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    sendInActiveConversation(input, draftAttachments);
  };

  const activeSuggestionsMessageId =
    !input.trim() && !isReplying
      ? [...chatMessages].reverse().find(message => message.suggestions?.length)
          ?.id
      : undefined;
  const latestMessage = chatMessages[chatMessages.length - 1];
  const awaitingAssistant =
    isReplying &&
    latestMessage?.role === 'assistant' &&
    !latestMessage.text &&
    !latestMessage.artifacts?.length &&
    !latestMessage.confirm &&
    !latestMessage.form;
  const workingLabel = activity ?? workingVerbs[language][workingVerbIndex];
  const showWorkingActivity =
    Boolean(activity) ||
    (awaitingAssistant && toolSteps.every(step => step.state !== 'running'));
  const normalizedPopularActionQuery = popularActionQuery.trim().toLowerCase();
  const filteredPopularActionGroups = demoPopularActionGroups[language]
    .map(group => ({
      ...group,
      items: normalizedPopularActionQuery
        ? group.items.filter(item =>
            `${item.label} ${item.prompt}`
              .toLowerCase()
              .includes(normalizedPopularActionQuery)
          )
        : group.items,
    }))
    .filter(group => group.items.length > 0);

  return (
    <section
      ref={sectionRef}
      className="bg-[var(--color-background)] py-20 lg:py-28"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 max-w-3xl">
          <div className="mb-4 flex items-center gap-3 text-sm font-bold uppercase tracking-[0.18em] text-indigo-600 dark:text-indigo-300">
            <span className="h-px w-10 bg-indigo-500" aria-hidden="true" />
            {text.eyebrow}
          </div>
          <h2 className="text-4xl font-bold tracking-tight text-[var(--color-foreground)] lg:text-5xl">
            {text.title}
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-[var(--color-foreground-secondary)] lg:text-xl">
            {text.description}
          </p>
        </div>

        <div className="overflow-hidden rounded-[2rem] border border-[var(--color-card-border)] bg-[var(--color-card-background)] shadow-[0_30px_80px_-40px_rgba(49,46,129,0.45)]">
          <div className="grid lg:h-[700px] lg:max-h-[700px] lg:grid-cols-[minmax(320px,0.82fr)_minmax(0,1.38fr)]">
            <div className="min-w-0 border-b border-[var(--color-card-border)] bg-indigo-50/70 p-6 dark:bg-indigo-950/20 sm:p-8 lg:h-full lg:min-h-0 lg:overflow-y-auto lg:border-b-0 lg:border-r lg:p-10">
              <div className="mb-8 flex items-center gap-3">
                <Image
                  src={obieAnimatedAvatar}
                  alt=""
                  width={48}
                  height={48}
                  unoptimized
                  className="h-12 w-12 rounded-2xl object-cover shadow-lg shadow-indigo-500/20"
                />
                <div>
                  <p className="escape !text-base !leading-5 font-bold text-[var(--color-foreground)]">
                    {text.assistant}
                  </p>
                  <p className="escape flex items-center gap-1.5 whitespace-nowrap !text-[11px] !leading-4 text-[var(--color-foreground-secondary)]">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    {text.assistantRole} · {text.online}
                  </p>
                </div>
              </div>

              <div
                className="space-y-3"
                aria-label={
                  language === 'en' ? 'AI capabilities' : 'AI 功能類型'
                }
              >
                {scenarioOrder.map((scenarioKey, index) => {
                  const item = text.scenarios[scenarioKey];
                  const isActive = scenarioKey === activeScenario;

                  return (
                    <button
                      key={scenarioKey}
                      type="button"
                      aria-pressed={isActive}
                      onClick={() => selectScenario(scenarioKey)}
                      className={`w-full rounded-2xl border p-4 text-left transition ${
                        isActive
                          ? 'border-indigo-300 bg-white shadow-md dark:border-indigo-700 dark:bg-gray-900'
                          : 'border-transparent hover:border-indigo-200 hover:bg-white/70 dark:hover:border-indigo-800 dark:hover:bg-gray-900/60'
                      }`}
                    >
                      <span className="flex items-start gap-4">
                        <span
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                            isActive
                              ? 'bg-indigo-600 text-white'
                              : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-200'
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span>
                          <span className="block font-bold text-[var(--color-foreground)]">
                            {item.label}
                          </span>
                          <span className="mt-1 block text-sm leading-relaxed text-[var(--color-foreground-secondary)]">
                            {item.caption}
                          </span>
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <Link
                href={`/${language}/products/ai-overview/`}
                className="mt-8 inline-flex items-center gap-2 font-bold text-indigo-700 hover:text-indigo-900 dark:text-indigo-300 dark:hover:text-indigo-100"
              >
                {text.explore}
                <span aria-hidden="true">→</span>
              </Link>
            </div>

            <div
              className="relative grid min-h-[620px] min-w-0 self-stretch grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden bg-gradient-to-br from-white via-white to-indigo-50/60 dark:from-gray-950 dark:via-gray-950 dark:to-indigo-950/30 lg:h-full lg:min-h-0"
              aria-live="polite"
            >
              <div className="flex min-h-[65px] items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-800 sm:px-8">
                <span className="text-xs font-bold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                  {activeScenario === 'try'
                    ? text.interactive.badge
                    : text.guidedExample}
                </span>
                <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 dark:bg-indigo-950 dark:text-indigo-200">
                  {text.scenarios[activeScenario].label}
                </span>
              </div>

              <div
                ref={threadRef}
                className="flex min-h-0 flex-col gap-4 overflow-y-auto p-5 sm:p-8"
              >
                {guidedScenario && (
                  <div className="flex flex-col gap-7 lg:px-2 lg:py-2">
                    <div className="ml-auto max-w-[82%] rounded-2xl rounded-br-md bg-indigo-600 px-5 py-3 text-[13px] leading-5 text-white shadow-sm sm:text-sm sm:leading-6">
                      {guidedScenario.prompt}
                    </div>

                    <div className="flex gap-3 sm:gap-4">
                      <Image
                        src="/assets/images/ai/obie-default.webp"
                        alt=""
                        width={34}
                        height={34}
                        className="mt-0.5 h-[34px] w-[34px] shrink-0 rounded-xl object-cover"
                      />
                      <div className="min-w-0 flex-1">
                        <h3 className="!text-base font-bold !leading-6 text-gray-950 dark:text-white">
                          {guidedScenario.responseTitle}
                        </h3>
                        <p className="escape mt-2 max-w-2xl !text-[13px] !leading-5 text-gray-600 dark:text-gray-300">
                          {guidedScenario.response}
                        </p>

                        {activeScenario === 'business' && (
                          <div className="mt-6 border-t border-gray-200 pt-6 dark:border-gray-800">
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-0 sm:divide-x divide-gray-200 dark:divide-gray-700">
                              {guidedScenarios.business.stats.map(stat => (
                                <div
                                  key={stat.label}
                                  className="sm:px-3 first:pl-0 last:pr-0"
                                >
                                  <p className="escape !text-xl !leading-6 font-black tracking-tight text-gray-950 dark:text-white">
                                    {stat.value}
                                  </p>
                                  <p className="escape mt-1 !text-xs !leading-4 text-gray-500 dark:text-gray-400">
                                    {stat.label}
                                  </p>
                                </div>
                              ))}
                            </div>

                            <div className="mt-6">
                              <div className="mb-3 flex items-center justify-between gap-4">
                                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                                  {guidedScenarios.business.chartLabel}
                                </span>
                                <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">
                                  {guidedScenarios.business.chartNote}
                                </span>
                              </div>
                              <div
                                className="flex h-24 items-end gap-3 border-b border-gray-200 dark:border-gray-700"
                                role="group"
                                aria-label={
                                  language === 'en'
                                    ? 'Live business attention signals'
                                    : '即時營運注意訊號'
                                }
                              >
                                {guidedScenarios.business.chart.map(item => (
                                  <span
                                    key={item.label}
                                    className="flex h-full flex-1 flex-col justify-end"
                                  >
                                    <span className="mb-1 text-center text-[10px] font-bold text-gray-600 dark:text-gray-300">
                                      {item.value}
                                    </span>
                                    <span
                                      className="w-full rounded-t-md bg-gradient-to-t from-indigo-600 to-blue-400"
                                      style={{
                                        height: `${Math.max(8, (item.value / businessChartMax) * 78)}%`,
                                      }}
                                    />
                                  </span>
                                ))}
                              </div>
                              <div
                                className="mt-2 grid gap-3 text-center text-[10px] text-gray-400 dark:text-gray-500"
                                style={{
                                  gridTemplateColumns: `repeat(${Math.max(1, guidedScenarios.business.chart.length)}, minmax(0, 1fr))`,
                                }}
                              >
                                {guidedScenarios.business.chart.map(item => (
                                  <span key={item.label}>{item.label}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {activeScenario === 'table' && (
                          <div className="mt-6 overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
                            <div className="flex items-center justify-between gap-4 border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800/80">
                              <span className="text-xs font-bold text-gray-700 dark:text-gray-200">
                                {guidedScenarios.table.tableSummary}
                              </span>
                              <span className="flex h-6 items-center rounded-full bg-amber-100 px-2 text-[11px] font-bold text-amber-800 dark:bg-amber-950 dark:text-amber-200">
                                {language === 'en'
                                  ? 'Needs review'
                                  : '需要查看'}
                              </span>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="min-w-[580px] w-full border-collapse text-left text-xs">
                                <thead className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                  <tr>
                                    {guidedScenarios.table.tableColumns.map(
                                      column => (
                                        <th
                                          key={column}
                                          className="border-b border-gray-100 px-4 py-2.5 font-semibold dark:border-gray-800"
                                        >
                                          {column}
                                        </th>
                                      )
                                    )}
                                  </tr>
                                </thead>
                                <tbody>
                                  {guidedScenarios.table.tableRows.map(row => {
                                    const [name, visits] = row[0].split('|');
                                    return (
                                      <tr
                                        key={name}
                                        className="border-b border-gray-100 last:border-0 dark:border-gray-800"
                                      >
                                        <td className="px-4 py-3">
                                          <span className="block font-bold text-gray-900 dark:text-white">
                                            {name}
                                          </span>
                                          <span className="mt-0.5 block text-[11px] text-gray-400">
                                            {visits}
                                          </span>
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 text-gray-600 dark:text-gray-300">
                                          {row[1]}
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 text-gray-600 dark:text-gray-300">
                                          {row[2]}
                                        </td>
                                        <td className="px-4 py-3">
                                          <span className="whitespace-nowrap rounded-full bg-rose-50 px-2.5 py-1 font-semibold text-rose-700 dark:bg-rose-950/60 dark:text-rose-300">
                                            {row[3]}
                                          </span>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {activeScenario === 'staff' && (
                          <div className="mt-6 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
                            <div className="bg-gradient-to-r from-indigo-50 via-white to-blue-50 px-4 py-4 dark:from-indigo-950/60 dark:via-gray-900 dark:to-blue-950/50 sm:px-5">
                              <div className="flex items-center justify-between gap-4">
                                <div className="flex min-w-0 items-center gap-3">
                                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-gradient-to-br from-indigo-600 to-blue-500 text-sm font-black text-white shadow-md shadow-indigo-200 dark:shadow-none">
                                    {staffInitials || 'OB'}
                                  </span>
                                  <span className="min-w-0">
                                    <strong className="block truncate text-sm text-gray-950 dark:text-white">
                                      {guidedScenarios.staff.instructorName}
                                    </strong>
                                    <span className="mt-0.5 block truncate text-[11px] text-gray-500 dark:text-gray-400">
                                      {guidedScenarios.staff.locationName}
                                    </span>
                                  </span>
                                </div>
                                <span
                                  className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold ${
                                    guidedScenarios.staff.appointmentOpen
                                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300'
                                      : 'bg-gray-200 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                                  }`}
                                >
                                  <span
                                    className={`h-1.5 w-1.5 rounded-full ${
                                      guidedScenarios.staff.appointmentOpen
                                        ? 'bg-emerald-500'
                                        : 'bg-gray-400'
                                    }`}
                                  />
                                  {guidedScenarios.staff.appointmentOpen
                                    ? language === 'en'
                                      ? 'Accepting bookings'
                                      : '可接受預約'
                                    : language === 'en'
                                      ? 'Bookings closed'
                                      : '預約已關閉'}
                                </span>
                              </div>

                              <div className="mt-4 border-t border-indigo-100 pt-4 dark:border-indigo-900/70">
                                <div className="mb-3 flex items-center justify-between gap-3">
                                  <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-indigo-700 dark:text-indigo-300">
                                    {language === 'en'
                                      ? 'Appointment flow'
                                      : '預約流程'}
                                  </span>
                                  <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400">
                                    {guidedScenarios.staff.connectedCount}{' '}
                                    {language === 'en'
                                      ? 'services connected'
                                      : '項服務已連結'}
                                  </span>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                  {appointmentFlow.map((label, index) => (
                                    <div
                                      key={label}
                                      className="relative flex min-w-0 flex-col items-center text-center"
                                    >
                                      {index < appointmentFlow.length - 1 && (
                                        <span className="absolute left-[calc(50%+14px)] right-[calc(-50%+14px)] top-3 h-px bg-indigo-200 dark:bg-indigo-800" />
                                      )}
                                      <span className="relative z-[1] grid h-6 w-6 place-items-center rounded-full bg-indigo-600 text-[10px] font-black text-white ring-4 ring-indigo-50 dark:ring-indigo-950">
                                        {index + 1}
                                      </span>
                                      <span className="mt-2 max-w-[110px] text-[10px] font-semibold leading-3.5 text-gray-600 dark:text-gray-300">
                                        {label}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>

                            <div>
                              <div className="flex items-center justify-between px-4 py-2.5 sm:px-5">
                                <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400">
                                  {language === 'en'
                                    ? 'Connected services'
                                    : '已連結服務'}
                                </span>
                                <span className="text-[10px] text-gray-400">
                                  {language === 'en'
                                    ? 'Duration · price'
                                    : '時間 · 價格'}
                                </span>
                              </div>
                              <div className="divide-y divide-gray-100 border-t border-gray-100 dark:divide-gray-800 dark:border-gray-800">
                                {guidedScenarios.staff.steps.map(step => (
                                  <div
                                    key={step.number}
                                    className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 sm:px-5"
                                  >
                                    <span className="grid h-8 w-8 place-items-center rounded-lg bg-indigo-50 text-[11px] font-black text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300">
                                      {step.number.padStart(2, '0')}
                                    </span>
                                    <span className="min-w-0">
                                      <strong className="block truncate text-[12px] text-gray-900 dark:text-gray-100">
                                        {step.title}
                                      </strong>
                                      <span className="mt-0.5 flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400">
                                        <span>{step.duration}</span>
                                        <span aria-hidden="true">·</span>
                                        <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                                          {language === 'en'
                                            ? 'Connected'
                                            : '已連結'}
                                        </span>
                                      </span>
                                    </span>
                                    <strong className="whitespace-nowrap text-right text-[11px] text-gray-800 dark:text-gray-200">
                                      {step.price}
                                    </strong>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {chatMessages.length === 0 && !input.trim() && (
                          <div className="mt-6">
                            <p className="escape mb-2 !text-[9px] !leading-none font-semibold uppercase tracking-[0.1em] text-gray-400 dark:text-gray-500">
                              {text.askNext}
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {guidedScenario.followups.map(followup => (
                                <DemoQuickReply
                                  key={followup.label}
                                  label={followup.label}
                                  disabled={!showcase}
                                  onSelect={() =>
                                    sendInActiveConversation(
                                      followup.livePrompt,
                                      [],
                                      'guided_followup'
                                    )
                                  }
                                />
                              ))}
                            </div>
                          </div>
                        )}
                        {showcaseLoadFailed && chatMessages.length === 0 && (
                          <p className="escape mt-3 !text-[11px] !leading-4 text-rose-600 dark:text-rose-300">
                            {text.interactive.unavailable}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {guidedScenario && chatMessages.length > 0 && (
                  <div className="border-t border-gray-200 dark:border-gray-800" />
                )}

                {chatMessages.map(message => {
                  if (message.role === 'user') {
                    return (
                      <div
                        key={message.id}
                        className="ml-auto max-w-[82%] rounded-2xl rounded-br-md bg-indigo-600 px-3 py-3 text-[13px] leading-5 text-white shadow-sm sm:text-sm sm:leading-6"
                      >
                        {message.attachments?.length ? (
                          <div className="mb-2 flex flex-wrap gap-2">
                            {message.attachments.map(attachment =>
                              attachment.previewUrl ? (
                                <Image
                                  key={attachment.id}
                                  src={attachment.previewUrl}
                                  alt={attachment.name}
                                  width={80}
                                  height={80}
                                  unoptimized
                                  className="h-20 w-20 rounded-xl object-cover"
                                />
                              ) : (
                                <span
                                  key={attachment.id}
                                  className="max-w-[210px] truncate rounded-lg bg-white/15 px-2.5 py-1.5 text-[11px] font-semibold"
                                >
                                  ◻ {attachment.name}
                                </span>
                              )
                            )}
                          </div>
                        ) : null}
                        {message.text && (
                          <span className="whitespace-pre-wrap">
                            {message.text}
                          </span>
                        )}
                      </div>
                    );
                  }
                  if (
                    !message.text &&
                    !message.artifacts?.length &&
                    !message.confirm &&
                    !message.form
                  ) {
                    return null;
                  }
                  return (
                    <div key={message.id} className="flex gap-3 sm:gap-4">
                      <Image
                        src="/assets/images/ai/obie-default.webp"
                        alt=""
                        width={34}
                        height={34}
                        className="mt-0.5 h-[34px] w-[34px] shrink-0 rounded-xl object-cover"
                      />
                      <div className="min-w-0 max-w-[88%]">
                        <div className="rounded-2xl rounded-tl-md bg-indigo-50 px-5 py-3 text-[13px] leading-5 text-gray-700 dark:bg-indigo-950/50 dark:text-gray-200 sm:text-sm sm:leading-6">
                          {message.text && (
                            <div
                              className="[&_.ob-md-table-wrap]:my-3 [&_.ob-md-table-wrap]:w-full [&_.ob-md-table-wrap]:overflow-x-auto [&_.ob-md-table-wrap]:rounded-xl [&_.ob-md-table-wrap]:border [&_.ob-md-table-wrap]:border-indigo-100 dark:[&_.ob-md-table-wrap]:border-indigo-800 [&_a]:font-semibold [&_a]:text-indigo-700 [&_a]:underline dark:[&_a]:text-indigo-300 [&_code]:rounded [&_code]:bg-indigo-100 [&_code]:px-1 [&_code]:py-0.5 dark:[&_code]:bg-indigo-900 [&_h3]:mb-2 [&_h3]:!text-sm [&_h3]:font-bold [&_h3]:!leading-5 [&_h4]:mb-2 [&_h4]:!text-sm [&_h4]:font-bold [&_h4]:!leading-5 [&_h5]:mb-2 [&_h5]:!text-sm [&_h5]:font-bold [&_h5]:!leading-5 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:space-y-1 [&_ol]:pl-5 [&_p]:!text-[13px] [&_p]:!leading-5 [&_p+p]:mt-3 [&_table]:w-full [&_table]:min-w-[560px] [&_table]:border-collapse [&_table]:text-xs [&_td]:border-b [&_td]:border-indigo-100 [&_td]:bg-white/70 [&_td]:px-3 [&_td]:py-2.5 [&_td]:align-top dark:[&_td]:border-indigo-900 dark:[&_td]:bg-gray-900/60 [&_th]:border-b [&_th]:border-indigo-100 [&_th]:bg-indigo-100/70 [&_th]:px-3 [&_th]:py-2.5 [&_th]:text-left [&_th]:text-[11px] [&_th]:font-bold dark:[&_th]:border-indigo-800 dark:[&_th]:bg-indigo-900/60 [&_ul]:my-3 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5 sm:[&_p]:!text-sm sm:[&_p]:!leading-6"
                              dangerouslySetInnerHTML={{
                                __html: renderChatMarkdownBlocks(message.text),
                              }}
                            />
                          )}
                          {message.artifacts?.map(artifact => (
                            <DemoArtifactTable
                              key={artifact.source}
                              artifact={artifact}
                            />
                          ))}
                          {message.confirm && (
                            <DemoConfirmCard
                              confirmation={message.confirm}
                              labels={text.interactive}
                              disabled={isReplying}
                              onDecision={approved =>
                                void decideChange(
                                  message.id,
                                  message.confirm as IDemoAdminConfirm,
                                  approved
                                )
                              }
                            />
                          )}
                          {message.form && (
                            <DemoFormCard
                              form={message.form}
                              submitLabel={text.interactive.submit}
                              onSubmit={answers =>
                                submitForm(
                                  message.id,
                                  message.form?.requestId ?? '',
                                  answers
                                )
                              }
                            />
                          )}
                        </div>
                        {message.id === activeSuggestionsMessageId &&
                        message.suggestions?.length ? (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {message.suggestions.map(suggestion => (
                              <DemoQuickReply
                                key={suggestion}
                                label={suggestion}
                                onSelect={() =>
                                  sendInActiveConversation(
                                    suggestion,
                                    [],
                                    'suggestion'
                                  )
                                }
                              />
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}

                {toolSteps.length > 0 && (
                  <div className="ml-12 space-y-1 px-1 sm:ml-[50px]">
                    {toolSteps.map((step, index) => (
                      <div
                        key={`${step.tool}-${index}`}
                        className={`flex items-center gap-2 text-[12px] leading-5 ${
                          step.state === 'error'
                            ? 'text-rose-600 dark:text-rose-300'
                            : 'text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        {step.state === 'running' ? (
                          <span className="h-2.5 w-2.5 animate-spin rounded-full border border-gray-300 border-t-indigo-600 dark:border-gray-700 dark:border-t-indigo-300" />
                        ) : (
                          <span aria-hidden="true">×</span>
                        )}
                        <span>{step.label}</span>
                      </div>
                    ))}
                  </div>
                )}

                {showWorkingActivity && (
                  <div className="flex items-center gap-3 sm:gap-4">
                    <Image
                      src="/assets/images/ai/obie-default.webp"
                      alt=""
                      width={34}
                      height={34}
                      className="h-[34px] w-[34px] shrink-0 rounded-xl object-cover"
                    />
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span className="flex gap-1" aria-hidden="true">
                        {[0, 1, 2].map(index => (
                          <span
                            key={index}
                            className="h-1.5 w-1.5 animate-pulse rounded-full bg-indigo-500"
                            style={{ animationDelay: `${index * 140}ms` }}
                          />
                        ))}
                      </span>
                      {workingLabel}…
                    </div>
                  </div>
                )}

                {sessionError && (
                  <div className="ml-12 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200 sm:ml-[50px]">
                    {sessionError}
                  </div>
                )}

                {activeScenario === 'try' &&
                  chatMessages.length === 1 &&
                  !isReplying &&
                  !input.trim() && (
                    <div className="ml-12 sm:ml-[50px]">
                      <p className="escape mb-2 !text-[9px] !leading-none font-semibold uppercase tracking-[0.1em] text-gray-400 dark:text-gray-500">
                        {text.interactive.tryAsking}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {interactiveQuickReplies.map(reply => (
                          <DemoQuickReply
                            key={reply.label}
                            label={reply.label}
                            disabled={!showcase}
                            onSelect={() =>
                              sendInActiveConversation(
                                reply.prompt,
                                [],
                                'quick_reply'
                              )
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}
              </div>

              <div className="bg-white px-3 pb-3 pt-2 dark:bg-gray-950">
                <form
                  onSubmit={handleSubmit}
                  onDragOver={event => {
                    event.preventDefault();
                    if (!isReplying) setDraggingAttachment(true);
                  }}
                  onDragLeave={() => setDraggingAttachment(false)}
                  onDrop={handleAttachmentDrop}
                  className={`relative flex flex-col gap-1 rounded-[14px] border bg-white py-2 pl-3.5 pr-2 transition dark:bg-gray-900 ${
                    draggingAttachment
                      ? 'border-indigo-500 bg-indigo-50/50 dark:border-indigo-400 dark:bg-indigo-950/30'
                      : 'border-gray-200 focus-within:border-indigo-400 dark:border-gray-700 dark:focus-within:border-indigo-500'
                  }`}
                >
                  {popularActionsOpen && (
                    <>
                      <button
                        type="button"
                        aria-label={
                          language === 'en'
                            ? 'Close popular actions'
                            : '關閉常用操作'
                        }
                        className="fixed inset-0 z-20 cursor-default"
                        onClick={() => {
                          setPopularActionsOpen(false);
                          setPopularActionQuery('');
                        }}
                      />
                      <div className="absolute bottom-[calc(100%+8px)] left-0 z-30 flex max-h-[360px] w-[min(320px,calc(100vw-3rem))] flex-col overflow-hidden rounded-[14px] border border-gray-200 bg-white shadow-[0_10px_34px_rgba(17,24,39,0.16)] dark:border-gray-700 dark:bg-gray-900">
                        <label className="flex items-center gap-2 border-b border-gray-100 px-3 py-2.5 dark:border-gray-800">
                          <Icon
                            icon="magnifying-glass"
                            className="h-4 w-4 shrink-0 text-gray-400"
                          />
                          <input
                            type="search"
                            autoFocus
                            value={popularActionQuery}
                            placeholder={text.interactive.searchActions}
                            onChange={event =>
                              setPopularActionQuery(event.target.value)
                            }
                            className="min-w-0 flex-1 border-0 bg-transparent text-[13px] text-gray-900 outline-none placeholder:text-gray-400 dark:text-white dark:placeholder:text-gray-500"
                          />
                        </label>
                        <div className="overflow-y-auto p-1.5">
                          {filteredPopularActionGroups.map(group => (
                            <div key={group.heading}>
                              <div className="px-2 pb-1 pt-2 text-[11px] font-bold uppercase tracking-[0.04em] text-gray-400 dark:text-gray-500">
                                {group.heading}
                              </div>
                              {group.items.map(action => (
                                <button
                                  key={action.label}
                                  type="button"
                                  onClick={() =>
                                    sendInActiveConversation(
                                      action.prompt,
                                      [],
                                      'popular_action'
                                    )
                                  }
                                  className="flex w-full items-center gap-2.5 rounded-[9px] px-2 py-2 text-left text-[13px] text-gray-700 transition hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
                                >
                                  <Icon
                                    icon={action.icon}
                                    className="h-[17px] w-[17px] shrink-0 text-gray-500 dark:text-gray-400"
                                  />
                                  <span>{action.label}</span>
                                </button>
                              ))}
                            </div>
                          ))}
                          {filteredPopularActionGroups.length === 0 && (
                            <div className="px-3 py-4 text-center text-[13px] text-gray-400 dark:text-gray-500">
                              {text.interactive.noMatchingActions}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={event => {
                      setInput(event.target.value);
                      setPopularActionsOpen(false);
                      setPopularActionQuery('');
                      if (event.target.value.trim())
                        clearQuickReplies(activeScenario);
                    }}
                    onKeyDown={handleKeyDown}
                    onPaste={handleAttachmentPaste}
                    rows={1}
                    maxLength={2000}
                    placeholder={text.ask}
                    aria-label={text.ask}
                    className="max-h-40 min-h-[38px] w-full resize-none bg-transparent py-1 text-[13px] leading-[1.45] text-gray-900 outline-none placeholder:text-[13px] placeholder:text-gray-400 dark:text-white dark:placeholder:text-gray-500"
                  />
                  {draftAttachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 py-1">
                      {draftAttachments.map(attachment => (
                        <div
                          key={attachment.id}
                          className="relative flex h-16 min-w-16 max-w-[180px] items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800"
                        >
                          {attachment.previewUrl ? (
                            <Image
                              src={attachment.previewUrl}
                              alt={attachment.name}
                              width={64}
                              height={64}
                              unoptimized
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <span className="truncate px-3 text-[10px] font-semibold text-gray-600 dark:text-gray-300">
                              {attachment.name}
                            </span>
                          )}
                          <button
                            type="button"
                            aria-label={`Remove ${attachment.name}`}
                            onClick={() =>
                              setDraftAttachments(current =>
                                current.filter(
                                  item => item.id !== attachment.id
                                )
                              )
                            }
                            className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-gray-950/75 text-xs text-white transition hover:bg-gray-950"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {attachmentError && (
                    <p className="escape mt-2 !text-[10px] !leading-4 text-rose-600 dark:text-rose-300">
                      {attachmentError}
                    </p>
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <button
                        type="button"
                        disabled={isReplying}
                        onClick={() =>
                          setPopularActionsOpen(open => {
                            if (open) setPopularActionQuery('');
                            return !open;
                          })
                        }
                        className="flex items-center gap-1 bg-transparent py-0.5 text-[12px] font-semibold text-gray-500 transition hover:text-gray-700 disabled:opacity-40 dark:text-gray-400 dark:hover:text-gray-200"
                      >
                        <span>{text.interactive.popular}</span>
                        <Icon
                          icon={
                            popularActionsOpen ? 'chevron-up' : 'chevron-down'
                          }
                          className="h-3 w-3 shrink-0"
                        />
                      </button>
                      <button
                        type="button"
                        disabled={
                          isReplying ||
                          draftAttachments.length >= maxAttachments
                        }
                        aria-label={text.interactive.attach}
                        title={text.interactive.attach}
                        onClick={() => fileInputRef.current?.click()}
                        className="flex h-6 w-6 items-center justify-center text-gray-500 transition hover:text-gray-700 disabled:opacity-40 dark:text-gray-400 dark:hover:text-gray-200"
                      >
                        <svg
                          aria-hidden="true"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          className="h-3.5 w-3.5"
                        >
                          <path
                            d="m20.5 11.5-8.7 8.7a6 6 0 0 1-8.5-8.5l9.2-9.2a4 4 0 1 1 5.7 5.7L9 17.4a2 2 0 0 1-2.8-2.8l8.5-8.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif,text/csv,.csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        multiple
                        hidden
                        onChange={handleFileChange}
                      />
                      {chatMessages.length > 1 && (
                        <button
                          type="button"
                          disabled={isReplying}
                          aria-label={text.interactive.restart}
                          title={text.interactive.restart}
                          onClick={() => void restartConversation()}
                          className="flex h-7 w-7 items-center justify-center text-gray-500 transition hover:text-gray-700 disabled:opacity-40 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          <svg
                            aria-hidden="true"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            className="h-4 w-4"
                          >
                            <path
                              d="M4 4v6h6M5.6 15.5A8 8 0 1 0 6 7.4L4 10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                    <button
                      type="submit"
                      disabled={
                        (!input.trim() && draftAttachments.length === 0) ||
                        isReplying
                      }
                      aria-label={text.interactive.send}
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[7px] bg-gray-950 text-base font-bold text-white transition hover:brightness-125 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 dark:bg-white dark:text-gray-950 dark:disabled:bg-gray-800 dark:disabled:text-gray-500"
                    >
                      ↑
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
